import { ReactNode, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { Button, Checkbox } from '@zep/ui';
import { useChannelTalk } from '../../../../../lib/ChannelTalk';
import { useAgreeTerms } from '../../../../../services';
import { PrivacyJson, TermsJson } from '../../../../../types/AccountsTypes';
import S from './AgreeTerms.module.scss';
export const AgreeTerms = ({
  privacyData,
  termsData
}: {
  privacyData: PrivacyJson;
  termsData: TermsJson;
}) => {
  const {
    locale = 'ko'
  } = useRouter();
  const {
    t
  } = useTranslation(['accounts']);
  const [isShowFixedBar, setShowFixedBar] = useState(true);
  const [[checkedTerms, checkedPrivacy], setChecked] = useState([false, false]);
  const isAgree = checkedTerms && checkedPrivacy;
  const channelTalk = useChannelTalk();
  useEffect(() => {
    if (channelTalk !== null) {
      channelTalk.hideChannelButton();
    }
  }, [channelTalk]);
  useEffect(() => {
    if (!isShowFixedBar && channelTalk !== null) {
      channelTalk.showChannelButton();
    }
  }, [isShowFixedBar, channelTalk]);
  const {
    privacyContent,
    termsContent
  } = (() => {
    return {
      privacyContent: privacyData.privacy,
      termsContent: termsData.termsNormal
    };
  })();
  const handleChecked = (checked: 'all' | 'privacy' | 'terms') => {
    if (checked === 'all') {
      setChecked(([terms, privacy]) => terms && privacy ? [false, false] : [true, true]);
      return;
    }
    checked === 'terms' ? setChecked(([terms, privacy]) => [!terms, privacy]) : setChecked(([terms, privacy]) => [terms, !privacy]);
  };
  return <div className={S.wrapper} data-sentry-component="AgreeTerms" data-sentry-source-file="AgreeTerms.tsx">
      <div className={S.container}>
        <Logo data-sentry-element="Logo" data-sentry-source-file="AgreeTerms.tsx" />
        <div className={S.content}>
          <div className={S.content_title}>
            {t('accounts:agreeTerms.content.title')}
          </div>
          <div className={S.content_checkboxes}>
            <div>
              <div className={S.sub_title}>
                {t('accounts:agreeTerms.content.subtitle')}
              </div>
            </div>
            <div className={S.content_items}>
              <CheckWithContent link={`https://docs.channel.io/zep-support/${locale}/articles/b40ac18f`} title={t('accounts:agreeTerms.checkboxTerms.label')} content={termsContent} checked={checkedTerms} onChangeCheckbox={() => handleChecked('terms')} data-sentry-element="CheckWithContent" data-sentry-source-file="AgreeTerms.tsx" />
              <CheckWithContent title={t('accounts:agreeTerms.checkboxPrivacy.label')} link={`https://docs.channel.io/zep-support/${locale}/articles/8232ed3d`} content={privacyContent} checked={checkedPrivacy} onChangeCheckbox={() => handleChecked('privacy')} data-sentry-element="CheckWithContent" data-sentry-source-file="AgreeTerms.tsx" />
            </div>
          </div>
          <Footer onChangeCheckBox={() => handleChecked('all')} isAgree={isAgree} data-sentry-element="Footer" data-sentry-source-file="AgreeTerms.tsx" />
        </div>
      </div>
      {isShowFixedBar && <FixedBar onClose={() => setShowFixedBar(false)} />}
    </div>;
};
const CheckWithContent = ({
  title,
  link,
  content: _content,
  checked,
  onChangeCheckbox
}: {
  link: string;
  title: ReactNode;
  content: string;
  checked: boolean;
  onChangeCheckbox: () => void;
}) => {
  const {
    t
  } = useTranslation(['accounts']);
  // 연속된 br 제거 및 hr 제거
  const content = _content.replace(/(<br\s*\/?>)+/g, '<br>').replace(/<(hr)\s*\/?>/g, '');
  return <div className={S.content_item} data-sentry-component="CheckWithContent" data-sentry-source-file="AgreeTerms.tsx">
      <div className={S.content_item_header}>
        <div>
          <Checkbox label={title} checked={checked} onChange={onChangeCheckbox} data-sentry-element="Checkbox" data-sentry-source-file="AgreeTerms.tsx" />
        </div>
        <div className={S.content_item_header_link}>
          <Link target={'_blank'} href={link} prefetch={false} data-sentry-element="Link" data-sentry-source-file="AgreeTerms.tsx">
            {t('accounts:agreeTerms.content.more')}
          </Link>
        </div>
      </div>
      <div className={S.content_item_textarea_wrapper}>
        <div className={S.content_item_textarea} dangerouslySetInnerHTML={{
        __html: content
      }}></div>
      </div>
    </div>;
};
const Footer = ({
  onChangeCheckBox,
  isAgree
}: {
  onChangeCheckBox: () => void;
  isAgree: boolean;
}) => {
  const {
    login
  } = useAuth();
  const {
    t
  } = useTranslation(['accounts']);
  const {
    replace: replaceUrl,
    pathname,
    query,
    isReady
  } = useRouter();
  const {
    mutateAsync: agreeTerms
  } = useAgreeTerms();
  const [payload, setPayload] = useState({
    token: '',
    redirectURL: '/'
  });
  useEffect(() => {
    function isJsonString(str: string): boolean {
      try {
        const parsed = JSON.parse(str);
        return typeof parsed === 'object' && parsed !== null;
      } catch (error) {
        return false;
      }
    }
    const getRedirectURL = (): string => {
      const redirectURL = String(query.redirectURL) || '/';
      // 구글로그인시 저렇게 와서 방어코드를 넣습니다
      // redirectURL={%22redirectUrl%22:%22/home/spaces%22,%22origin%22:%22https://dev-2.zep.us%22}
      if (isJsonString(redirectURL)) {
        const redirectObj = JSON.parse(redirectURL);
        return redirectObj?.redirectUrl || redirectObj?.redirectURL || '/';
      }
      return redirectURL;
    };
    setPayload({
      token: String(query.token ?? ''),
      redirectURL: getRedirectURL()
    });
  }, [pathname, query]);
  useEffect(() => {
    if (!isReady) return;
    if (!query.token) {
      replaceUrl('/');
      return;
    }
  }, [isReady, query, replaceUrl]);
  const handleSubmit = async () => {
    try {
      const {
        player
      } = await agreeTerms({
        token: payload.token
      });

      // @ts-ignore
      await login(player);
      await replaceUrl(String(payload.redirectURL || '/'));
    } catch (e) {
      console.error(e);
    }
  };
  return <div className={S.footer} data-sentry-component="Footer" data-sentry-source-file="AgreeTerms.tsx">
      <Checkbox label={t('accounts:agreeTerms.checkboxAll.label')} onChange={onChangeCheckBox} checked={isAgree} data-sentry-element="Checkbox" data-sentry-source-file="AgreeTerms.tsx" />
      <Button size={'md'} className="w-[160px]" disabled={!isAgree} onClick={handleSubmit} data-sentry-element="Button" data-sentry-source-file="AgreeTerms.tsx">
        {t('accounts:agreeTerms.checkboxAll.button')}
      </Button>
    </div>;
};
const FixedBar = ({
  onClose
}: {
  onClose: () => void;
}) => {
  const {
    t
  } = useTranslation(['accounts']);
  return <div className={S.fixed_bar} data-sentry-component="FixedBar" data-sentry-source-file="AgreeTerms.tsx">
      <div className={S.fixed_bar_left} dangerouslySetInnerHTML={{
      __html: t('accounts:agreeTerms.fixedBar.content')
    }}></div>
      <div className={S.fixed_bar_button}>
        <Button className="w-[160px]" variant={'sub'} onClick={onClose} data-sentry-element="Button" data-sentry-source-file="AgreeTerms.tsx">
          {t('accounts:agreeTerms.fixedBar.close')}
        </Button>
      </div>
    </div>;
};
const Logo = () => <div className={S.logo} data-sentry-component="Logo" data-sentry-source-file="AgreeTerms.tsx">
    <img alt="zep-logo" src="/images/light/layout/logo_zep.svg" width={112} height={40} />
  </div>;